import { useEffect, useRef, type ReactNode, type TouchEvent as ReactTouchEvent } from "react";
import { AnimatePresence, motion, useReducedMotion, type Transition } from "../../lib/motion/index.js";

const DRAWER_SPRING: Transition = { type: "spring", stiffness: 360, damping: 34 };
/** Horizontal travel toward the edge that commits a swipe-close. */
const CLOSE_PX = 48;

interface Props {
  side: "left" | "right";
  open: boolean;
  onClose: () => void;
  /** Accessible name for the drawer dialog. */
  label: string;
  /** Tailwind width classes for the panel (defaults to a wide drawer). */
  widthClassName?: string;
  /** Temporarily hide the drawer (scrim + panel) without unmounting — used while
   *  a child item is being touch-dragged out onto the page behind it. */
  transparent?: boolean;
  /** Keep the scrim visible (dim) but pass touches THROUGH it to the content
   *  behind, while the panel stays interactive — lets the calendar grid receive
   *  long-press block-drags while the inbox drawer is open. The owner must
   *  provide its own tap-to-close (the scrim's onClick no longer fires). */
  scrimPassthrough?: boolean;
  children: ReactNode;
}

/**
 * Mobile overlay drawer: slides in from a screen edge over a scrim. Closes on
 * scrim tap, a swipe back toward its edge, or Escape (which the Android back
 * button synthesizes — the panel carries role="dialog" for that contract).
 */
export function MobileDrawer({ side, open, onClose, label, widthClassName = "w-[82vw] max-w-80", transparent = false, scrimPassthrough = false, children }: Props) {
  const reduce = useReducedMotion();
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const node = panelRef.current;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    node?.addEventListener("keydown", onKey);
    window.addEventListener("keydown", onKey);
    return () => { node?.removeEventListener("keydown", onKey); window.removeEventListener("keydown", onKey); };
  }, [open, onClose]);

  function onTouchStart(e: ReactTouchEvent) {
    const t = e.touches[0];
    touchStart.current = t ? { x: t.clientX, y: t.clientY } : null;
  }
  function onTouchMove(e: ReactTouchEvent) {
    const s = touchStart.current;
    const t = e.touches[0];
    if (!s || !t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dy) > Math.abs(dx)) return;
    const travel = side === "left" ? -dx : dx;
    if (travel > CLOSE_PX) { touchStart.current = null; onClose(); }
  }

  const hidden = side === "left" ? "-100%" : "100%";
  return (
    <AnimatePresence>
      {open && (
        // When passing through, the FULL-SCREEN container must also be
        // pointer-events-none — it would otherwise swallow touches on the area
        // outside the panel before they reach the grid behind; the panel
        // re-enables pointer events below so it stays interactive.
        <div className={`fixed inset-0 z-40 ${transparent || scrimPassthrough ? "pointer-events-none" : ""}`}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: transparent ? 0 : 1 }}
            exit={{ opacity: 0 }}
            className={`absolute inset-0 bg-black/40 ${scrimPassthrough ? "pointer-events-none" : ""}`}
            onClick={onClose}
            onTouchMove={onTouchMove}
            onTouchStart={onTouchStart}
          />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={label}
            tabIndex={-1}
            initial={reduce ? false : { x: hidden }}
            animate={{ x: 0, opacity: transparent ? 0 : 1 }}
            exit={reduce ? { x: 0, opacity: 0 } : { x: hidden }}
            transition={reduce ? { duration: 0 } : DRAWER_SPRING}
            className={`absolute inset-y-0 flex ${widthClassName} flex-col overflow-y-auto bg-card p-3 shadow-xl ${side === "left" ? "left-0 border-r" : "right-0 border-l"} ${transparent ? "pointer-events-none" : scrimPassthrough ? "pointer-events-auto" : ""}`}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
          >
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
